import * as THREE from 'three';
import type { Viewmodel } from '../player/Viewmodel';
import { VIEWMODEL_TUNING } from '../player/ViewmodelAssets';

export type ViewmodelCalibId = 'mp5' | 'ak47';

interface PoseSnapshot {
  hipPos: THREE.Vector3;
  hipRot: THREE.Euler;
  adsPos: THREE.Vector3;
  adsRot: THREE.Euler;
  meshRot: THREE.Euler;
  meshPos: THREE.Vector3;
  meshScale: number;
  muzzleHip: THREE.Vector3;
  muzzleAds: THREE.Vector3;
  casingHip: THREE.Vector3;
  casingAds: THREE.Vector3;
}

interface SliderRow {
  range: HTMLInputElement;
  num: HTMLInputElement;
  read: () => number;
  write: (n: number) => void;
  original: () => number;
}

/**
 * Empty-studio first-person pose editor.
 * Open with `?calib=mp5` or `?calib=ak47` (also `?calib=viewmodel&weapon=ak47`).
 */
export class ViewmodelCalib {
  private readonly panel: HTMLElement;
  private readonly snippetEl: HTMLPreElement;
  private readonly holdTitle: HTMLElement;
  private readonly statusEl: HTMLElement;
  private readonly onKey: (e: KeyboardEvent) => void;

  private weapon: ViewmodelCalibId;
  private aiming = false;
  private active = false;
  private originals = new Map<ViewmodelCalibId, PoseSnapshot>();
  private holdRows: SliderRow[] = [];
  private meshRows: SliderRow[] = [];
  private muzzleRows: SliderRow[] = [];
  private casingRows: SliderRow[] = [];

  constructor(
    private readonly viewmodel: Viewmodel,
    initial: ViewmodelCalibId,
  ) {
    this.weapon = initial;
    this.panel = document.createElement('div');
    this.panel.id = 'vm-calib-panel';
    this.panel.className = 'vm-calib-panel hidden';
    this.panel.innerHTML = panelMarkup();
    document.getElementById('app')?.appendChild(this.panel);

    this.snippetEl = this.panel.querySelector('#vm-calib-snippet') as HTMLPreElement;
    this.holdTitle = this.panel.querySelector('#vm-calib-hold-title') as HTMLElement;
    this.statusEl = this.panel.querySelector('#vm-calib-status') as HTMLElement;

    this.bindChrome();
    this.onKey = (e) => this.handleKey(e);
  }

  get isActive(): boolean {
    return this.active;
  }

  start(): void {
    this.captureOriginals();
    this.equip(this.weapon, false);
    this.viewmodel.enableCalibFreeze(true);
    this.viewmodel.debugSetAds(0);
    this.buildSliders();
    this.refreshChrome();
    this.refreshSnippet();
    this.panel.classList.remove('hidden');
    this.active = true;
    window.addEventListener('keydown', this.onKey);
    this.statusEl.textContent =
      'Orange cone = muzzle flash. Brass capsule = casing eject. Click to fire. Hip and ADS are separate.';
  }

  update(): void {
    if (!this.active) return;
    this.refreshSnippet();
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKey);
    this.viewmodel.enableCalibFreeze(false);
    this.panel.remove();
    this.active = false;
  }

  private captureOriginals(): void {
    for (const id of ['mp5', 'ak47'] as const) {
      const pose = this.viewmodel.poseFor(id);
      const mesh = this.viewmodel.getMeshTune(id);
      const tune = VIEWMODEL_TUNING[id];
      const fx = this.viewmodel.fxFor(id);
      this.originals.set(id, {
        hipPos: pose.hipPos.clone(),
        hipRot: pose.hipRot.clone(),
        adsPos: pose.adsPos.clone(),
        adsRot: pose.adsRot.clone(),
        meshRot: (mesh?.rot.clone() ?? tune.rot.clone()),
        meshPos: (mesh?.pos.clone() ?? tune.pos.clone()),
        meshScale: mesh?.scl.x ?? tune.scale,
        muzzleHip: fx?.muzzleHip.clone() ?? new THREE.Vector3(),
        muzzleAds: fx?.muzzleAds.clone() ?? new THREE.Vector3(),
        casingHip: fx?.casingHip.clone() ?? new THREE.Vector3(),
        casingAds: fx?.casingAds.clone() ?? new THREE.Vector3(),
      });
    }
  }

  private bindChrome(): void {
    this.panel.querySelector('#vm-calib-mp5')?.addEventListener('click', () => this.equip('mp5'));
    this.panel.querySelector('#vm-calib-ak47')?.addEventListener('click', () => this.equip('ak47'));
    this.panel.querySelector('#vm-calib-hip')?.addEventListener('click', () => this.setAiming(false));
    this.panel.querySelector('#vm-calib-ads')?.addEventListener('click', () => this.setAiming(true));
    this.panel.querySelector('#vm-calib-copy')?.addEventListener('click', () => void this.copySnippet());
    this.panel.querySelector('#vm-calib-reset-hold')?.addEventListener('click', () => this.resetHold());
    this.panel.querySelector('#vm-calib-reset-mesh')?.addEventListener('click', () => this.resetMesh());
    this.panel.querySelector('#vm-calib-reset-muzzle')?.addEventListener('click', () => this.resetMuzzle());
    this.panel.querySelector('#vm-calib-reset-casing')?.addEventListener('click', () => this.resetCasing());
    this.panel.querySelector('#vm-calib-reset-all')?.addEventListener('click', () => this.resetAll());
  }

  private handleKey(e: KeyboardEvent): void {
    if (!this.active) return;
    if ((e.target as HTMLElement | null)?.closest?.('input, textarea, select')) return;
    if (e.code === 'KeyA' || e.code === 'Tab') {
      e.preventDefault();
      this.setAiming(!this.aiming);
    }
    if (e.code === 'Digit1') this.equip('mp5');
    if (e.code === 'Digit2') this.equip('ak47');
    if (e.code === 'KeyC' && (e.ctrlKey || e.metaKey)) return;
  }

  private equip(id: ViewmodelCalibId, rebuild = true): void {
    this.weapon = id;
    this.viewmodel.debugEquip(id);
    this.viewmodel.debugSetAds(this.aiming ? 1 : 0);
    history.replaceState(null, '', `?calib=${id}`);
    if (rebuild) {
      this.buildSliders();
      this.refreshChrome();
      this.refreshSnippet();
    }
  }

  private setAiming(on: boolean): void {
    this.aiming = on;
    this.viewmodel.debugSetAds(on ? 1 : 0);
    this.buildSliders();
    this.refreshChrome();
    this.refreshSnippet();
  }

  private refreshChrome(): void {
    this.panel.querySelector('#vm-calib-mp5')?.classList.toggle('active', this.weapon === 'mp5');
    this.panel.querySelector('#vm-calib-ak47')?.classList.toggle('active', this.weapon === 'ak47');
    this.panel.querySelector('#vm-calib-hip')?.classList.toggle('active', !this.aiming);
    this.panel.querySelector('#vm-calib-ads')?.classList.toggle('active', this.aiming);
    this.holdTitle.textContent = this.aiming ? 'ADS pose (looking down sights)' : 'Hip pose (not aiming)';
    const muzzleTitle = this.panel.querySelector('#vm-calib-muzzle-title');
    if (muzzleTitle) {
      muzzleTitle.textContent = this.aiming ? 'Muzzle flash — ADS' : 'Muzzle flash — hip';
    }
    const casingTitle = this.panel.querySelector('#vm-calib-casing-title');
    if (casingTitle) {
      casingTitle.textContent = this.aiming ? 'Casing eject — ADS' : 'Casing eject — hip';
    }
    const title = this.panel.querySelector('#vm-calib-title');
    if (title) title.textContent = `${this.weapon.toUpperCase()} viewmodel studio`;
  }

  private buildSliders(): void {
    const holdHost = this.panel.querySelector('#vm-calib-hold-sliders') as HTMLElement;
    const meshHost = this.panel.querySelector('#vm-calib-mesh-sliders') as HTMLElement;
    const muzzleHost = this.panel.querySelector('#vm-calib-muzzle-sliders') as HTMLElement;
    const casingHost = this.panel.querySelector('#vm-calib-casing-sliders') as HTMLElement;
    holdHost.replaceChildren();
    meshHost.replaceChildren();
    muzzleHost.replaceChildren();
    casingHost.replaceChildren();
    this.holdRows = [];
    this.meshRows = [];
    this.muzzleRows = [];
    this.casingRows = [];

    const pose = this.viewmodel.poseFor(this.weapon);
    const orig = this.originals.get(this.weapon)!;
    const holdPos = this.aiming ? pose.adsPos : pose.hipPos;
    const holdRot = this.aiming ? pose.adsRot : pose.hipRot;
    const origPos = this.aiming ? orig.adsPos : orig.hipPos;
    const origRot = this.aiming ? orig.adsRot : orig.hipRot;

    const holdDefs: Array<{
      label: string;
      min: number;
      max: number;
      step: number;
      hint: string;
      read: () => number;
      write: (n: number) => void;
      original: () => number;
    }> = [
      {
        label: 'Pos X',
        min: -0.25,
        max: 0.3,
        step: 0.001,
        hint: 'right',
        read: () => holdPos.x,
        write: (n) => {
          holdPos.x = n;
        },
        original: () => origPos.x,
      },
      {
        label: 'Pos Y',
        min: -0.35,
        max: 0.12,
        step: 0.001,
        hint: 'up',
        read: () => holdPos.y,
        write: (n) => {
          holdPos.y = n;
        },
        original: () => origPos.y,
      },
      {
        label: 'Pos Z',
        min: -0.55,
        max: -0.08,
        step: 0.001,
        hint: 'away',
        read: () => holdPos.z,
        write: (n) => {
          holdPos.z = n;
        },
        original: () => origPos.z,
      },
      {
        label: 'Pitch',
        min: -0.8,
        max: 0.8,
        step: 0.005,
        hint: 'muzzle up',
        read: () => holdRot.x,
        write: (n) => {
          holdRot.x = n;
        },
        original: () => origRot.x,
      },
      {
        label: 'Yaw',
        min: -0.8,
        max: 0.8,
        step: 0.005,
        hint: 'stock left',
        read: () => holdRot.y,
        write: (n) => {
          holdRot.y = n;
        },
        original: () => origRot.y,
      },
      {
        label: 'Roll',
        min: -0.8,
        max: 0.8,
        step: 0.005,
        hint: 'stock up',
        read: () => holdRot.z,
        write: (n) => {
          holdRot.z = n;
        },
        original: () => origRot.z,
      },
    ];

    for (const d of holdDefs) {
      this.holdRows.push(this.mountSlider(holdHost, d));
    }

    const mesh = this.viewmodel.getMeshTune(this.weapon);
    if (!mesh) return;

    const meshDefs = [
      {
        label: 'Pitch',
        min: -Math.PI,
        max: Math.PI,
        step: 0.01,
        hint: 'mesh',
        read: () => mesh.rot.x,
        write: (n: number) => {
          mesh.rot.x = n;
          this.applyMesh();
        },
        original: () => orig.meshRot.x,
      },
      {
        label: 'Yaw',
        min: -Math.PI,
        max: Math.PI,
        step: 0.01,
        hint: 'mesh',
        read: () => mesh.rot.y,
        write: (n: number) => {
          mesh.rot.y = n;
          this.applyMesh();
        },
        original: () => orig.meshRot.y,
      },
      {
        label: 'Roll',
        min: -Math.PI,
        max: Math.PI,
        step: 0.01,
        hint: 'mesh',
        read: () => mesh.rot.z,
        write: (n: number) => {
          mesh.rot.z = n;
          this.applyMesh();
        },
        original: () => orig.meshRot.z,
      },
      {
        label: 'Pos X',
        min: -0.2,
        max: 0.2,
        step: 0.001,
        hint: '',
        read: () => mesh.pos.x,
        write: (n: number) => {
          mesh.pos.x = n;
          this.applyMesh();
        },
        original: () => orig.meshPos.x,
      },
      {
        label: 'Pos Y',
        min: -0.2,
        max: 0.2,
        step: 0.001,
        hint: '',
        read: () => mesh.pos.y,
        write: (n: number) => {
          mesh.pos.y = n;
          this.applyMesh();
        },
        original: () => orig.meshPos.y,
      },
      {
        label: 'Pos Z',
        min: -0.2,
        max: 0.2,
        step: 0.001,
        hint: '',
        read: () => mesh.pos.z,
        write: (n: number) => {
          mesh.pos.z = n;
          this.applyMesh();
        },
        original: () => orig.meshPos.z,
      },
      {
        label: 'Scale',
        min: 0.08,
        max: 0.55,
        step: 0.005,
        hint: '',
        read: () => this.viewmodel.getMeshTune(this.weapon)?.scl.x ?? orig.meshScale,
        write: (n: number) => {
          const m = this.viewmodel.getMeshTune(this.weapon);
          if (m) m.scl.setScalar(n);
          this.applyMesh();
        },
        original: () => orig.meshScale,
      },
    ];

    for (const d of meshDefs) {
      this.meshRows.push(this.mountSlider(meshHost, d));
    }

    const fx = this.viewmodel.fxFor(this.weapon);
    if (!fx) return;
    const muzzle = this.aiming ? fx.muzzleAds : fx.muzzleHip;
    const casing = this.aiming ? fx.casingAds : fx.casingHip;
    const origMuzzle = this.aiming ? orig.muzzleAds : orig.muzzleHip;
    const origCasing = this.aiming ? orig.casingAds : orig.casingHip;

    const axis = (
      label: string,
      hint: string,
      vec: THREE.Vector3,
      origVec: THREE.Vector3,
      key: 'x' | 'y' | 'z',
    ) => ({
      label,
      min: key === 'z' ? -0.75 : -0.28,
      max: key === 'z' ? 0.08 : 0.28,
      step: 0.001,
      hint,
      read: () => vec[key],
      write: (n: number) => {
        vec[key] = n;
        this.viewmodel.syncCalibFxPreview();
      },
      original: () => origVec[key],
    });

    for (const d of [
      axis('Pos X', 'right', muzzle, origMuzzle, 'x'),
      axis('Pos Y', 'up', muzzle, origMuzzle, 'y'),
      axis('Pos Z', 'away (−Z = barrel)', muzzle, origMuzzle, 'z'),
    ]) {
      this.muzzleRows.push(this.mountSlider(muzzleHost, d));
    }
    for (const d of [
      axis('Pos X', 'right (eject)', casing, origCasing, 'x'),
      axis('Pos Y', 'up', casing, origCasing, 'y'),
      axis('Pos Z', 'toward camera', casing, origCasing, 'z'),
    ]) {
      this.casingRows.push(this.mountSlider(casingHost, d));
    }
    this.viewmodel.syncCalibFxPreview();
  }

  private mountSlider(
    host: HTMLElement,
    def: {
      label: string;
      min: number;
      max: number;
      step: number;
      hint: string;
      read: () => number;
      write: (n: number) => void;
      original: () => number;
    },
  ): SliderRow {
    const row = document.createElement('div');
    row.className = 'vm-slider-row';

    const lab = document.createElement('span');
    lab.className = 'vm-slider-label';
    lab.textContent = def.label;
    lab.title = def.hint;

    const range = document.createElement('input');
    range.type = 'range';
    range.min = String(def.min);
    range.max = String(def.max);
    range.step = String(def.step);

    const num = document.createElement('input');
    num.type = 'text';
    num.inputMode = 'decimal';
    num.autocomplete = 'off';
    num.spellcheck = false;
    num.className = 'vm-slider-num';

    const reset = document.createElement('button');
    reset.type = 'button';
    reset.className = 'vm-reset-one';
    reset.title = `Reset ${def.label}`;
    reset.textContent = '↺';

    const apply = (raw: number) => {
      const n = clamp(raw, def.min, def.max);
      def.write(n);
      range.value = String(n);
      num.value = n.toFixed(3);
      this.refreshSnippet();
    };

    range.addEventListener('input', () => apply(Number(range.value)));
    num.addEventListener('input', () => {
      const n = parseLocaleNumber(num.value);
      if (n == null) return;
      apply(n);
    });
    num.addEventListener('change', () => {
      const n = parseLocaleNumber(num.value);
      apply(n ?? def.read());
    });
    reset.addEventListener('click', () => apply(def.original()));

    const v = def.read();
    range.value = String(v);
    num.value = v.toFixed(3);

    row.append(lab, range, num, reset);
    host.appendChild(row);
    return { range, num, read: def.read, write: def.write, original: def.original };
  }

  private applyMesh(): void {
    const mesh = this.viewmodel.getMeshTune(this.weapon);
    if (!mesh) return;
    this.viewmodel.debugOrient(
      this.weapon,
      mesh.rot.x,
      mesh.rot.y,
      mesh.rot.z,
      mesh.scl.x,
      [mesh.pos.x, mesh.pos.y, mesh.pos.z],
    );
  }

  private resetHold(): void {
    const pose = this.viewmodel.poseFor(this.weapon);
    const orig = this.originals.get(this.weapon)!;
    if (this.aiming) {
      pose.adsPos.copy(orig.adsPos);
      pose.adsRot.copy(orig.adsRot);
    } else {
      pose.hipPos.copy(orig.hipPos);
      pose.hipRot.copy(orig.hipRot);
    }
    this.buildSliders();
    this.refreshSnippet();
  }

  private resetMesh(): void {
    const orig = this.originals.get(this.weapon)!;
    this.viewmodel.debugOrient(
      this.weapon,
      orig.meshRot.x,
      orig.meshRot.y,
      orig.meshRot.z,
      orig.meshScale,
      [orig.meshPos.x, orig.meshPos.y, orig.meshPos.z],
    );
    this.buildSliders();
    this.refreshSnippet();
  }

  private resetMuzzle(): void {
    const fx = this.viewmodel.fxFor(this.weapon);
    const orig = this.originals.get(this.weapon)!;
    if (!fx) return;
    if (this.aiming) fx.muzzleAds.copy(orig.muzzleAds);
    else fx.muzzleHip.copy(orig.muzzleHip);
    this.viewmodel.syncCalibFxPreview();
    this.buildSliders();
    this.refreshSnippet();
  }

  private resetCasing(): void {
    const fx = this.viewmodel.fxFor(this.weapon);
    const orig = this.originals.get(this.weapon)!;
    if (!fx) return;
    if (this.aiming) fx.casingAds.copy(orig.casingAds);
    else fx.casingHip.copy(orig.casingHip);
    this.viewmodel.syncCalibFxPreview();
    this.buildSliders();
    this.refreshSnippet();
  }

  private resetAll(): void {
    const pose = this.viewmodel.poseFor(this.weapon);
    const orig = this.originals.get(this.weapon)!;
    pose.hipPos.copy(orig.hipPos);
    pose.hipRot.copy(orig.hipRot);
    pose.adsPos.copy(orig.adsPos);
    pose.adsRot.copy(orig.adsRot);
    this.viewmodel.debugOrient(
      this.weapon,
      orig.meshRot.x,
      orig.meshRot.y,
      orig.meshRot.z,
      orig.meshScale,
      [orig.meshPos.x, orig.meshPos.y, orig.meshPos.z],
    );
    const fx = this.viewmodel.fxFor(this.weapon);
    if (fx) {
      fx.muzzleHip.copy(orig.muzzleHip);
      fx.muzzleAds.copy(orig.muzzleAds);
      fx.casingHip.copy(orig.casingHip);
      fx.casingAds.copy(orig.casingAds);
      this.viewmodel.syncCalibFxPreview();
    }
    this.buildSliders();
    this.refreshSnippet();
    this.statusEl.textContent = `Reset ${this.weapon} to code defaults.`;
  }

  private snippetText(): string {
    const id = this.weapon;
    const pose = this.viewmodel.poseFor(id);
    const mesh = this.viewmodel.getMeshTune(id);
    const hipName = id === 'mp5' ? 'mp5' : 'rifle';
    const fx = this.viewmodel.fxFor(id);
    const holdNow = this.holdRows.map((r) => r.read());
    const meshNow = this.meshRows.map((r) => r.read());
    const holdLabels = ['Pos X', 'Pos Y', 'Pos Z', 'Pitch', 'Yaw', 'Roll'];
    const meshLabels = ['Pitch', 'Yaw', 'Roll', 'Pos X', 'Pos Y', 'Pos Z', 'Scale'];
    const holdBlock = holdLabels
      .map((label, i) => `  ${label}: ${fmt(holdNow[i] ?? 0)}`)
      .join('\n');
    const meshBlock = meshLabels
      .map((label, i) => `  ${label}: ${fmt(meshNow[i] ?? 0)}`)
      .join('\n');
    const fxName = id === 'mp5' ? 'mp5' : 'rifle';
    const lines = [
      `=== ${id} viewmodel calib ===`,
      `aiming: ${this.aiming ? 'ADS' : 'hip'}`,
      '',
      `${this.aiming ? 'ADS' : 'Hip'} sliders now:`,
      holdBlock,
      '',
      'Mesh sliders now:',
      meshBlock,
      '',
      `${this.aiming ? 'ADS' : 'Hip'} muzzle: ${fmt(this.muzzleRows[0]?.read() ?? 0)}, ${fmt(this.muzzleRows[1]?.read() ?? 0)}, ${fmt(this.muzzleRows[2]?.read() ?? 0)}`,
      `${this.aiming ? 'ADS' : 'Hip'} casing: ${fmt(this.casingRows[0]?.read() ?? 0)}, ${fmt(this.casingRows[1]?.read() ?? 0)}, ${fmt(this.casingRows[2]?.read() ?? 0)}`,
      '',
      `${hipName}HipPos = new THREE.Vector3(${fmt(pose.hipPos.x)}, ${fmt(pose.hipPos.y)}, ${fmt(pose.hipPos.z)});`,
      `${hipName}HipRot = new THREE.Euler(${fmt(pose.hipRot.x)}, ${fmt(pose.hipRot.y)}, ${fmt(pose.hipRot.z)});`,
      `${hipName}AdsPos = new THREE.Vector3(${fmt(pose.adsPos.x)}, ${fmt(pose.adsPos.y)}, ${fmt(pose.adsPos.z)});`,
      `${hipName}AdsRot = new THREE.Euler(${fmt(pose.adsRot.x)}, ${fmt(pose.adsRot.y)}, ${fmt(pose.adsRot.z)});`,
      '',
      `${fxName}MuzzleHip = new THREE.Vector3(${fmt(fx?.muzzleHip.x ?? 0)}, ${fmt(fx?.muzzleHip.y ?? 0)}, ${fmt(fx?.muzzleHip.z ?? 0)});`,
      `${fxName}MuzzleAds = new THREE.Vector3(${fmt(fx?.muzzleAds.x ?? 0)}, ${fmt(fx?.muzzleAds.y ?? 0)}, ${fmt(fx?.muzzleAds.z ?? 0)});`,
      `${fxName}CasingHip = new THREE.Vector3(${fmt(fx?.casingHip.x ?? 0)}, ${fmt(fx?.casingHip.y ?? 0)}, ${fmt(fx?.casingHip.z ?? 0)});`,
      `${fxName}CasingAds = new THREE.Vector3(${fmt(fx?.casingAds.x ?? 0)}, ${fmt(fx?.casingAds.y ?? 0)}, ${fmt(fx?.casingAds.z ?? 0)});`,
      '',
      `VIEWMODEL_TUNING.${id}:`,
      `  rot: new THREE.Euler(${fmtRot(mesh?.rot.x ?? 0)}, ${fmtRot(mesh?.rot.y ?? 0)}, ${fmtRot(mesh?.rot.z ?? 0)}, 'YXZ'),`,
      `  pos: new THREE.Vector3(${fmt(mesh?.pos.x ?? 0)}, ${fmt(mesh?.pos.y ?? 0)}, ${fmt(mesh?.pos.z ?? 0)}),`,
      `  scale: ${fmt(mesh?.scl.x ?? 0)},`,
    ];
    return lines.join('\n');
  }

  private refreshSnippet(): void {
    this.snippetEl.textContent = this.snippetText();
  }

  private async copySnippet(): Promise<void> {
    const text = this.snippetText();
    try {
      await navigator.clipboard.writeText(text);
      this.statusEl.textContent = 'Copied — paste that block in chat and I will apply it.';
    } catch {
      console.log(text);
      this.statusEl.textContent = 'Clipboard blocked — select the box below, or check the console.';
    }
  }
}

function panelMarkup(): string {
  return `
    <div class="vm-calib-title" id="vm-calib-title">Viewmodel studio</div>
    <p class="vm-calib-status" id="vm-calib-status"></p>
    <div class="vm-calib-row">
      <button id="vm-calib-mp5" class="calib-btn" type="button">MP5</button>
      <button id="vm-calib-ak47" class="calib-btn" type="button">AK-47</button>
    </div>
    <div class="vm-calib-row">
      <button id="vm-calib-hip" class="calib-btn" type="button">Hip</button>
      <button id="vm-calib-ads" class="calib-btn" type="button">Aiming (ADS)</button>
    </div>
    <div class="vm-calib-section">
      <div class="vm-calib-section-title" id="vm-calib-hold-title">Hip pose</div>
      <div id="vm-calib-hold-sliders"></div>
      <button id="vm-calib-reset-hold" class="calib-btn vm-section-reset" type="button">Reset this pose</button>
    </div>
    <div class="vm-calib-section">
      <div class="vm-calib-section-title">Mesh in slot (stock / barrel)</div>
      <div id="vm-calib-mesh-sliders"></div>
      <button id="vm-calib-reset-mesh" class="calib-btn vm-section-reset" type="button">Reset mesh</button>
    </div>
    <div class="vm-calib-section">
      <div class="vm-calib-section-title" id="vm-calib-muzzle-title">Muzzle flash — hip</div>
      <div id="vm-calib-muzzle-sliders"></div>
      <button id="vm-calib-reset-muzzle" class="calib-btn vm-section-reset" type="button">Reset muzzle (this pose)</button>
    </div>
    <div class="vm-calib-section">
      <div class="vm-calib-section-title" id="vm-calib-casing-title">Casing eject — hip</div>
      <div id="vm-calib-casing-sliders"></div>
      <button id="vm-calib-reset-casing" class="calib-btn vm-section-reset" type="button">Reset casing (this pose)</button>
    </div>
    <div class="vm-calib-section">
      <div class="vm-calib-section-title">Copy these numbers</div>
      <pre id="vm-calib-snippet" class="calib-values vm-calib-snippet"></pre>
      <div class="vm-calib-row">
        <button id="vm-calib-reset-all" class="calib-btn" type="button">Reset all</button>
        <button id="vm-calib-copy" class="calib-btn primary" type="button">Copy snippet</button>
      </div>
    </div>
    <p class="calib-hint">A / Tab = hip ↔ ADS &nbsp;·&nbsp; 1 = MP5 &nbsp;·&nbsp; 2 = AK &nbsp;·&nbsp; click = fire<br>
    Orange cone = muzzle &nbsp;·&nbsp; brass = casing &nbsp;·&nbsp; ↺ resets one slider<br>
    URLs: <code>?calib=mp5</code> &nbsp; <code>?calib=ak47</code></p>
  `;
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

/** Accepts `0.029` and European `0,029`. */
function parseLocaleNumber(raw: string): number | null {
  const t = raw.trim().replace(/\s/g, '');
  if (!t || t === '-' || t === '.' || t === ',' || t === '-.' || t === '-,') return null;
  let s = t;
  if (s.includes(',') && s.includes('.')) {
    if (s.lastIndexOf(',') > s.lastIndexOf('.')) {
      s = s.replace(/\./g, '').replace(',', '.');
    } else {
      s = s.replace(/,/g, '');
    }
  } else {
    s = s.replace(',', '.');
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function fmt(n: number): string {
  const s = n.toFixed(3);
  return s === '-0.000' ? '0.000' : s;
}

function fmtRot(n: number): string {
  const half = Math.PI / 2;
  if (Math.abs(n + half) < 0.001) return '-Math.PI / 2';
  if (Math.abs(n - half) < 0.001) return 'Math.PI / 2';
  if (Math.abs(n - Math.PI) < 0.001) return 'Math.PI';
  if (Math.abs(n + Math.PI) < 0.001) return '-Math.PI';
  if (Math.abs(n) < 0.0005) return '0';
  return fmt(n);
}
